import { Form, Modal } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useState } from 'react';
import { ComplianceDocumentFileField } from '../../../../../shared/features/compliance-documents/components/ComplianceDocumentFileField';
import { ComplianceDocumentFormFields } from '../../../../../shared/features/compliance-documents/components/ComplianceDocumentFormFields';
import type { ComplianceDocumentEntry, ComplianceDocumentView } from '../../../../../shared/features/compliance-documents/policies/compliance-documents';
import type { DocumentFormInput } from '../hooks/use-truck-documents';

interface TruckDocumentModalProps {
  open: boolean;
  /** The document type being added to or renewed — its name is shown, but never changed here. */
  documentType: ComplianceDocumentView | null;
  submitting: boolean;
  onSubmit: (input: DocumentFormInput, isRenewal: boolean) => Promise<boolean>;
  onCancel: () => void;
}

interface FormValues {
  number: string;
  issuer: string;
  issueDate: Dayjs;
  expiryDate: Dayjs;
}

function toFormValues(current: ComplianceDocumentEntry): FormValues {
  return {
    number: current.number,
    issuer: current.issuer,
    issueDate: dayjs(current.issueDate),
    expiryDate: dayjs(current.expiryDate),
  };
}

/**
 * Attaches the first document for a type, or renews it — always a new row, never an edit of the
 * one it supersedes. Keyed by the parent on the document type being acted on, so opening it for a
 * different type remounts it with fresh state rather than an effect. A renewal pre-fills from the
 * document it supersedes, since it's usually the same issuer and number with a later expiry.
 */
export function TruckDocumentModal({ open, documentType, submitting, onSubmit, onCancel }: TruckDocumentModalProps) {
  const [form] = Form.useForm<FormValues>();
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState(false);
  const isRenewal = Boolean(documentType?.current);

  async function submit() {
    const values = await form.validateFields();
    if (!file) {
      setFileError(true);
      return;
    }
    if (!documentType) return;

    const submitted = await onSubmit(
      {
        documentTypeId: documentType.documentTypeId,
        number: values.number,
        issuer: values.issuer,
        issueDate: values.issueDate.format('YYYY-MM-DD'),
        expiryDate: values.expiryDate.format('YYYY-MM-DD'),
        file,
      },
      isRenewal,
    );
    if (submitted) setFile(null);
  }

  return (
    <Modal
      open={open}
      title={documentType ? `${isRenewal ? 'Renew' : 'Add'} ${documentType.documentTypeName}` : 'Add document'}
      onOk={submit}
      onCancel={onCancel}
      confirmLoading={submitting}
      destroyOnClose
      okText={isRenewal ? 'Renew' : 'Add'}
    >
      <Form<FormValues>
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={documentType?.current ? toFormValues(documentType.current) : undefined}
      >
        <ComplianceDocumentFileField
          file={file}
          error={fileError}
          onChange={(next) => {
            setFile(next);
            setFileError(false);
          }}
        />
        <ComplianceDocumentFormFields />
      </Form>
    </Modal>
  );
}
